import React, { useEffect, useState } from 'react';
import { translate } from '../../../services/translate';
import { Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { WindowInformation } from '../../../services/window-information';
import FormActions from '../../../components/FormActions';
import { dispatchService } from '../../../services/central-api/dispatch';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import MyDropDownPicker from '../../../components/MyDropDownPicker';
import { minutesToText } from '../../../services/time-helpers';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
    onClose: () => void;
    unit: PaginatedUnit;
}

function UnitPauseModal({ isModalVisible, setModalVisible, unit, onClose }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const [pauseReason, setPauseReason] = useState<number>(0);
    const [originalPauseReasons, setOriginalPauseReasons] = useState<PauseReason[]>([]);
    const [pauseReasonVisible, setPauseReasonVisible] = useState<boolean>(false);
    const [pauseReasons, setPauseReasons] = useState<{ label: string; value: number; }[]>([{
        label: '', value: 0
    }]);

    async function getPauseReasons() {
        try {
            const pauseReasons = await dispatchService.getPauseReasons(unit.id);
            setOriginalPauseReasons(pauseReasons);
            setPauseReasons(pauseReasons.map(p => {
                return { value: p.id, label: p.name };
            }));
        } catch (err) {
            console.error(err);
        }
    }

    async function pauseUnit() {
        try {
            if (unit) {
                await dispatchService.pauseUnpauseUnit(unit.id, unit.paused ? 'unpause' : 'pause', pauseReason);
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });
                setPauseReason(0);
                setModalVisible(false);
            }
            onClose();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    useEffect(() => {
        getPauseReasons();
    }, [unit]);

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                setModalVisible(false);
                setPauseReason(0);
                onClose();
            }}
        >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '20%' }]}>
                    <MyAppText>{`${translate('pauseTeam')}: ${unit?.unitName?.name || ''}`}</MyAppText>
                    <MyDropDownPicker
                        items={pauseReasons}
                        multiple={false}
                        value={pauseReason}
                        setValue={setPauseReason}
                        setOpen={setPauseReasonVisible}
                        open={pauseReasonVisible}
                        zIndex={3}
                    />
                    {pauseReason != 0 && <View style={{ flexDirection: 'row' }}>
                        <MyAppText>
                            {`${translate('pauseReasonPeriod')}: `}
                            <MyAppText style={styles.pauseReasonPeriod}>{minutesToText(originalPauseReasons.find(opr => opr.id == pauseReason)?.pauseMinutes ?? null, { longFormat: true })}</MyAppText>
                        </MyAppText>
                    </View>}
                    <FormActions
                        onSubmit={async () => {
                            setModalVisible(false);
                            await pauseUnit();
                        }}
                        confirmText='pause'
                        onClose={() => {
                            setModalVisible(false);
                            setPauseReason(0);
                            onClose();
                        }}
                        disabled={pauseReason == 0} />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },
    pauseReasonPeriod: {
        color: theme.colors.primaryButton.background,
        fontWeight: '600'
    }
}));


export default UnitPauseModal;
